/**
 * Connect an Account — client-side entity executor (ADR-029 §2).
 *
 * Runs in the HOST page, never in the plugin iframe. A plugin asks to connect
 * an outside account (X, LinkedIn, …); the host draws its own connect sheet —
 * platform-authored content, the "who draws the pixels" rule — where the person
 * connects a new account or lets this app use one they already connected. The
 * OAuth round-trip and the grant are made by the host session; the plugin only
 * learns the connection the person chose, or that they canceled.
 *
 * Host services arrive through `configureConnectionsConnectHost` at
 * registration (`client/src/lib/register-client-entities.ts`), because the
 * executor context carries only `{ userId, archeId }`.
 *
 * Expected failures are RESULTS, not throws: the bridge forwards only an
 * error's message, so `{ error: { code, message } }` is how a typed code
 * reaches the SDK (`useFiasConnections`).
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const CONNECTIONS_CONNECT_ENTITY_ID = 'ent_intg_connections_connect_000';

/** Provider ids are config keys on the server (`CONNECTION_PROVIDERS`). */
const PROVIDER_ID = /^[a-z][a-z0-9_]{0,31}$/;

export interface ConnectionsConnectInput {
  provider: string;
}

export type ConnectionsConnectErrorCode =
  | 'INVALID_INPUT'
  | 'CONNECT_UNAVAILABLE'
  | 'PERMISSION_DENIED'
  | 'SHEET_BUSY';

export type ConnectionsConnectResult =
  | { connectionId: string; provider: string }
  | { canceled: true }
  | { error: { code: ConnectionsConnectErrorCode; message: string } };

/** What the host page provides: the sheet, drawn for one arche. */
export interface ConnectionsConnectHost {
  showConnectSheet(request: {
    archeId: string;
    provider: string;
  }): Promise<ConnectionsConnectResult>;
}

let host: ConnectionsConnectHost | null = null;

/** Called once by the host page at registration. */
export function configureConnectionsConnectHost(next: ConnectionsConnectHost | null): void {
  host = next;
}

export async function connectionsConnectExecutor(
  input: unknown,
  context: ClientEntityInvocationContext,
): Promise<ConnectionsConnectResult> {
  const provider =
    input && typeof input === 'object' ? (input as { provider?: unknown }).provider : undefined;
  if (typeof provider !== 'string' || !PROVIDER_ID.test(provider)) {
    return { error: { code: 'INVALID_INPUT', message: 'provider must be a provider id like "x"' } };
  }
  if (!host) {
    return {
      error: { code: 'CONNECT_UNAVAILABLE', message: 'Connecting accounts is not available here' },
    };
  }
  return host.showConnectSheet({ archeId: context.archeId, provider });
}

export const connectionsConnectDefinition = {
  entityId: CONNECTIONS_CONNECT_ENTITY_ID,
  rateLimitPerMinute: 5,
  pluginPermission: 'connections:use',
  executor: connectionsConnectExecutor,
} as const satisfies ClientEntityDefinition<unknown, ConnectionsConnectResult>;
